import React, { useEffect, useRef, useState } from 'react';

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HOW_IT_WORKS_PATH = `${process.env.PUBLIC_URL || ''}/how-it-works.md`;

const HowItWorksModal: React.FC<HowItWorksModalProps> = ({ isOpen, onClose }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const hasLoaded = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    if (hasLoaded.current) return;

    const fetchContent = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(HOW_IT_WORKS_PATH);
        if (!response.ok) {
          throw new Error('Failed to load documentation');
        }
        const text = await response.text();
        setContent(text);
        hasLoaded.current = true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load content';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, [isOpen, reloadToken]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="prompt-modal-backdrop" onClick={onClose}>
      <div className="prompt-modal how-it-works-modal" onClick={(event) => event.stopPropagation()}>
        <div className="prompt-modal-header">
          <h2>How It Works</h2>
          <button className="modal-close-btn" onClick={onClose} title="Close">
            ✕
          </button>
        </div>
        <div className="prompt-modal-content markdown-modal-content">
          {loading && <div className="loading">Loading guide...</div>}
          {error && (
            <div className="error">
              <p>{error}</p>
              <button
                className="retry-btn"
                type="button"
                onClick={() => {
                  hasLoaded.current = false;
                  setContent('');
                  setError(null);
                  setReloadToken((token) => token + 1);
                }}
              >
                Retry
              </button>
            </div>
          )}
          {!loading && !error && content && (
            <div className="prompt-content-section">
              <div className="section-header">
                <h3>Prompt Content</h3>
              </div>
              <div className="prompt-display">
                <pre className="prompt-text how-it-works-text">{content}</pre>
              </div>
            </div>
          )}
          {!loading && !error && !content && (
            <div className="loading">Guide content is unavailable.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HowItWorksModal;
